import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, BriefcaseIcon, PackageIcon, SearchIcon, BellIcon, UserIcon, UsersIcon, ClockIcon, BookOpenIcon, DatabaseIcon, CalculatorIcon, NetworkIcon } from 'lucide-react';
import Filter from '../ui/Filter';
interface TopBarProps {
  filters: {
    dateRange: string;
    region: string;
    businessUnit: string;
    productLine: string;
    customer: string;
    timePeriod: string;
  };
  onFilterChange: (filterType: string, value: string) => void;
  activeSection: string;
  onNavigate?: (section: string) => void;
}
const TopBar: React.FC<TopBarProps> = ({
  filters,
  onFilterChange,
  activeSection,
  onNavigate
}) => {
  const handleFormulasClick = () => {
    onNavigate && onNavigate('Formulas Used');
  };
  const handleDataModelClick = () => {
    onNavigate && onNavigate('View Data Model');
  };
  return <>
      <style data-id="element-11648">
        {`
          @keyframes subtlePulse {
            0%, 100% { 
              transform: scale(1);
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            50% { 
              transform: scale(1.04);
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }
          }

          @keyframes strongShine {
            0% {
              left: -60%;
              opacity: 0;
            }
            20% {
              opacity: 1;
            }
            50% {
              left: 120%;
              opacity: 1;
            }
            100% {
              left: 120%;
              opacity: 0;
            }
          }

          .formula-button {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
            font-weight: 600;
            border-radius: 0.5rem;
            background: #EDE7F6;
            border: 1px solid #D1C4E9;
            color: #333;
            cursor: pointer;
            transition: all 0.3s ease-in-out;
            animation: subtlePulse 3s ease-in-out infinite;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            position: relative;
            overflow: hidden;
          }

          .formula-button::before {
            content: "";
            position: absolute;
            top: 0;
            left: -60%;
            width: 60%;
            height: 100%;
            background: linear-gradient(
              115deg,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 255, 255, 0.65) 50%,
              rgba(255, 255, 255, 0) 100%
            );
            filter: blur(3px);
            opacity: 0;
            pointer-events: none;
            z-index: 1;
            animation: strongShine 1.25s ease-out infinite;
            animation-delay: 3s;
          }

          .formula-button:hover {
            background: #E1D5F0;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          }

          .data-model-button {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
            font-weight: 600;
            border-radius: 0.5rem;
            background: #E8F5E9;
            border: 1px solid #C8E6C9;
            color: #333;
            cursor: pointer;
            transition: all 0.3s ease-in-out;
            animation: subtlePulse 3s ease-in-out infinite;
            animation-delay: 1.5s;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            position: relative;
            overflow: hidden;
          }

          .data-model-button::before {
            content: "";
            position: absolute;
            top: 0;
            left: -60%;
            width: 60%;
            height: 100%;
            background: linear-gradient(
              115deg,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 255, 255, 0.65) 50%,
              rgba(255, 255, 255, 0) 100%
            );
            filter: blur(3px);
            opacity: 0;
            pointer-events: none;
            z-index: 1;
            animation: strongShine 1.25s ease-out infinite;
            animation-delay: 4.5s;
          }

          .data-model-button:hover {
            background: #DDF0DE;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
          }

          .button-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            color: #666;
            position: relative;
            z-index: 2;
          }

          .button-text {
            color: #222;
            position: relative;
            z-index: 2;
          }

          @media (max-width: 768px) {
            .formula-button,
            .data-model-button {
              padding: 0.4rem 0.75rem;
              font-size: 0.75rem;
              gap: 0.375rem;
            }

            .button-text {
              display: none;
            }

            .mobile-button-text {
              display: block;
            }
          }
        `}
      </style>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between h-full px-3 md:px-6 py-2 md:py-0 gap-3 md:gap-0" data-id="element-11649">
        {/* Filters - Scrollable on mobile */}
        <div className="flex items-center gap-2 md:space-x-3 overflow-x-auto pb-2 md:pb-0 scrollbar-hide" data-id="element-11650">
          <Filter icon={<CalendarIcon size={16} data-id="element-11652" />} label="Date Range" value={filters.dateRange} options={['Last 30 Days', 'Last 12 Months', 'Year to Date', 'Custom']} onChange={value => onFilterChange('dateRange', value)} data-id="element-11651" />
          <Filter icon={<MapPinIcon size={16} data-id="element-11654" />} label="Region" value={filters.region} options={['All India', 'North', 'West', 'South', 'East']} onChange={value => onFilterChange('region', value)} data-id="element-11653" />
          <Filter icon={<PackageIcon size={16} data-id="element-11656" />} label="Product Line" value={filters.productLine} options={['All Products', 'Lighting', 'Electrical', 'Polymer', 'Safety']} onChange={value => onFilterChange('productLine', value)} data-id="element-11655" />
          <Filter icon={<UsersIcon size={16} data-id="element-11658" />} label="Customer" value={filters.customer} options={['All Customers', 'Maruti Suzuki', 'Hyundai', 'Tata Motors', 'Mahindra', 'Honda', 'Toyota']} onChange={value => onFilterChange('customer', value)} data-id="element-11657" />
          <Filter icon={<ClockIcon size={16} data-id="element-11660" />} label="Time Period" value={filters.timePeriod} options={['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly']} onChange={value => onFilterChange('timePeriod', value)} data-id="element-11659" />
          <Filter icon={<BriefcaseIcon size={16} data-id="element-11662" />} label="Business Unit" value={filters.businessUnit} options={['All Units', 'Lighting', 'Electronics', 'Interiors', 'Powertrain']} onChange={value => onFilterChange('businessUnit', value)} data-id="element-11661" />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 md:space-x-4 flex-wrap md:flex-nowrap" data-id="element-11663">
          <button onClick={handleFormulasClick} className="formula-button flex-1 md:flex-initial" data-id="element-11664">
            <span className="button-icon" data-id="element-11665">
              <CalculatorIcon size={16} strokeWidth={2} data-id="element-11666" />
            </span>
            <span className="button-text" data-id="element-11667">Formula's Used For All Reports</span>
            <span className="mobile-button-text hidden" data-id="element-11668">Formulas</span>
          </button>
          <button onClick={handleDataModelClick} className="data-model-button flex-1 md:flex-initial" data-id="element-11669">
            <span className="button-icon" data-id="element-11670">
              <NetworkIcon size={16} strokeWidth={2} data-id="element-11671" />
            </span>
            <span className="button-text" data-id="element-11672">View Data Model</span>
            <span className="mobile-button-text hidden" data-id="element-11673">Data Model</span>
          </button>
          <button className="hidden md:block p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100" data-id="element-11674">
            <SearchIcon size={20} data-id="element-11675" />
          </button>
          <button className="hidden md:block p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100" data-id="element-11676">
            <BellIcon size={20} data-id="element-11677" />
          </button>
          <div className="hidden md:flex h-8 w-8 rounded-full bg-gray-200 items-center justify-center" data-id="element-11678">
            <UserIcon size={16} className="text-gray-500" data-id="element-11679" />
          </div>
        </div>
      </div>
    </>;
};
export default TopBar;